import React from "react";
import { Alert, Button, Chip, Stack, Typography } from "@mui/joy";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { SocialPost } from "../../types/socialMedia";
export function SocialDeliveryStatus({ post }: { post: SocialPost }) {
  const client = useQueryClient();
  const retry = useMutation({
    mutationFn: (account: number) =>
      api.post(`/api/marketing/social-posts/${post.id}/retry-delivery/`, {
        accounts: [account],
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["social-posts"] }),
  });
  const delivery = post.platform_content?.delivery || {};
  const running = Boolean((post.platform_content as any)?.delivery_running);
  return (
    <Stack spacing={1}>
      {running && (
        <Alert color="primary">
          Publishing is in progress. If this remains unchanged, verify the
          destination before posting again.
        </Alert>
      )}
      {Object.entries(delivery).map(([id, result]) => (
        <Stack key={id} spacing={0.5}>
          <Typography level="body-xs">
            {post.platforms_detail?.find((a) => String(a.id) === id)
              ?.account_name || "Destination"}{" "}
            <Chip
              size="sm"
              color={
                result.status === "published"
                  ? "success"
                  : result.status === "failed" || result.status === "unknown"
                    ? "warning"
                    : "neutral"
              }
            >
              {result.status === "unknown"
                ? "Needs verification"
                : result.status}
            </Chip>
          </Typography>
          {result.error && (
            <Typography level="body-xs">{result.error}</Typography>
          )}
          {result.status === "failed" && (
            <Button
              size="sm"
              variant="outlined"
              disabled={running}
              loading={retry.isPending}
              onClick={() => retry.mutate(Number(id))}
            >
              Retry this destination
            </Button>
          )}
        </Stack>
      ))}
      {retry.isError && (
        <Alert color="danger">
          Could not queue a retry. Refresh the post and check its destination
          status.
        </Alert>
      )}
    </Stack>
  );
}
